import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageMeta from '@/components/base/PageMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import { movies, featuredMovies, hotMovies, newMovies, categories, faqData } from '@/mocks/movies';

const siteUrl = 'https://pipicinema.com';

const homeSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '屁屁影院',
  url: siteUrl,
  description: '屁屁影院是您最佳的在线观影平台，汇聚海量高清电影、电视剧、动漫及综艺节目，无需注册即可免费畅享。',
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${siteUrl}/category/dianying?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
  inLanguage: 'zh-CN',
};

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '屁屁影院',
  url: siteUrl,
  description: '屁屁影院 - 海量高清影片在线免费观看平台',
  sameAs: [siteUrl],
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'contact@pipicinema.com',
    contactType: 'customer service',
    availableLanguage: ['Chinese'],
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqData.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

export default function Home() {
  const [activeCategory, setActiveCategory] = useState('all');

  const featuredMoviesData = featuredMovies.map((id) => movies.find((m) => m.id === id)).filter(Boolean) as typeof movies;
  const hotMoviesData = hotMovies.map((id) => movies.find((m) => m.id === id)).filter(Boolean) as typeof movies;
  const newMoviesData = newMovies.map((id) => movies.find((m) => m.id === id)).filter(Boolean) as typeof movies;

  const filteredMovies = activeCategory === 'all'
    ? [...featuredMoviesData, ...hotMoviesData].slice(0, 12)
    : movies.filter((m) => m.category === activeCategory);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <PageMeta
        title="屁屁影院 - 海量高清影片在线免费观看 | 电影 电视剧 动漫 综艺"
        description="屁屁影院是您最佳的在线观影平台，汇聚海量高清电影、电视剧、动漫及综艺节目，无需注册即可免费畅享。屁屁影院为您提供流畅播放体验，影片资源每日更新，支持多终端访问，让您随时随地享受优质观影体验。"
        keywords="屁屁影院,屁屁影院在线观看,屁屁影院高清电影,屁屁影院免费视频,屁屁影院电视剧,屁屁影院最新资源,屁屁影院手机版"
        canonicalUrl={siteUrl}
        ogImage="https://readdy.ai/api/search-image?query=Chinese%20movie%20streaming%20website%20hero%20banner%20with%20cinema%20elements%2C%20film%20reels%2C%20popcorn%2C%20warm%20golden%20lighting%2C%20clean%20minimal%20background%2C%20modern%20web%20design%20aesthetic&width=1200&height=630&seq=pipi-og-home&orientation=landscape"
        schema={{ '@context': 'https://schema.org', '@graph': [orgSchema, homeSchema, faqSchema] }}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=cinematic%20movie%20theater%20interior%20with%20warm%20golden%20and%20amber%20lighting%2C%20film%20projector%20beam%2C%20dark%20theater%20seats%2C%20atmospheric%20cinema%20vibe%2C%20elegant%20composition%2C%20warm%20deep%20orange%20and%20dark%20brown%20tones%2C%20dramatic%20lighting%2C%20clean%20background&width=1800&height=1200&seq=pipi-hero-bg&orientation=landscape"
            alt="屁屁影院 - 在线观影平台"
            title="屁屁影院 - 海量高清影片"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/50"></div>

          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4 tracking-tight">
              屁屁影院
            </h1>
            <p className="text-base md:text-xl text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
              海量高清电影、电视剧、动漫、综艺，无需注册，免费畅享
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/dianying"
                onClick={scrollToTop}
                className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-circle-line"></i>
                开始观影
              </Link>
              <Link
                to="/category/dianshiju"
                onClick={scrollToTop}
                className="inline-flex items-center gap-2 px-8 py-3 bg-background-50/20 backdrop-blur-sm text-white border border-white/30 rounded-full font-medium text-sm hover:bg-background-50/30 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-tv-line"></i>
                热门剧集
              </Link>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-8 px-4 md:px-6 bg-background-100/50 border-b border-background-200/50">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
              <button
                onClick={() => setActiveCategory('all')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeCategory === 'all'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-50 text-foreground-600 hover:text-foreground-950 border border-background-200'
                }`}
              >
                全部推荐
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:text-foreground-950 border border-background-200'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Movie Grid */}
        <section className="py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                {activeCategory === 'all' ? '精选推荐' : categories.find((c) => c.slug === activeCategory)?.label}
              </h2>
              {activeCategory !== 'all' && (
                <Link
                  to={`/category/${activeCategory}`}
                  onClick={scrollToTop}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 whitespace-nowrap"
                >
                  查看全部
                  <i className="ri-arrow-right-line"></i>
                </Link>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-product-shop="">
              {filteredMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        </section>

        {/* Featured Section */}
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100/50">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                  热门推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-1">大家都在看的精彩内容</p>
              </div>
              <Link
                to="/category/dianying"
                onClick={scrollToTop}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 whitespace-nowrap"
              >
                更多推荐
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-product-shop="">
              {hotMoviesData.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        </section>

        {/* New Arrivals */}
        <section className="py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                  最新上线
                </h2>
                <p className="text-sm text-foreground-500 mt-1">每日更新，新鲜出炉</p>
              </div>
              <Link
                to="/category/dianshiju"
                onClick={scrollToTop}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 whitespace-nowrap"
              >
                更多新片
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-product-shop="">
              {newMoviesData.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100/50">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[
                { value: '10,000+', label: '海量影片' },
                { value: '99.9%', label: '播放成功率' },
                { value: '500万+', label: '每日观影人次' },
                { value: '24h', label: '资源更新时间' },
              ].map((stat, idx) => (
                <div key={idx}>
                  <div className="text-2xl md:text-3xl font-heading font-bold text-accent-500">{stat.value}</div>
                  <div className="text-sm text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 md:py-16 px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-2">
              常见问题
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              关于屁屁影院，您可能想了解的信息
            </p>

            <div className="space-y-3">
              {faqData.map((faq, idx) => (
                <details key={idx} className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100/50 transition-colors">
                    <span className="text-sm font-medium text-foreground-950 pr-4">{faq.question}</span>
                    <span className="text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0">
                      <i className="ri-arrow-down-s-line"></i>
                    </span>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-16 md:py-20 px-4 md:px-6 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=warm%20cinematic%20atmosphere%20with%20soft%20film%20grain%20texture%2C%20golden%20and%20amber%20abstract%20background%2C%20movie%20themed%20bokeh%20lights%2C%20elegant%20dark%20gradient%2C%20clean%20minimal%20style&width=1800&height=800&seq=pipi-cta-bg&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-background-950/70"></div>
          <div className="relative z-10 max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-3">
              立即开始您的观影之旅
            </h2>
            <p className="text-sm md:text-base text-background-300 mb-8">
              无需注册，无需付费，海量高清影片等你来发现
            </p>
            <Link
              to="/category/dianying"
              onClick={scrollToTop}
              className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-circle-fill"></i>
              免费观影
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}